import {
  Decision,
  FeatureFlagDecision,
  HackleEvent,
  HackleRemoteConfig,
  Properties,
  User
} from "../../../../core/internal/model/model"
import {
  HackleExperiment,
  HackleInAppMessageLink,
  HackleInAppMessageListener,
  HackleInAppMessageView,
  HacklePage
} from "../../../../public/model"
import { PropertyOperations } from "../../../property/PropertyOperations"
import { HackleSubscriptionOperations } from "../../../subscription/HackleSubscriptionOperations"
import { BrowserHackleClient, PageView } from "../../../index.browser"
import { InAppMessageBridge } from "./InAppMessageBridge"
import { InAppMessageUi } from "../InAppMessageUi"
import { InAppMessageView } from "../view/InAppMessageView"
import ObjectUtil from "../../../../core/internal/util/ObjectUtil"
import { toInAppMessageActionViewEvent, toInAppMessageHiddenActionViewEvent } from "./InAppMessageHtmlActionConverter"
import { TimeUtil } from "../../../../core/internal/util/TimeUtil"
import { IAM_HIDE_EXPIRATION_MILLIS } from "../../../../config"
import Logger from "../../../../core/internal/logger"

export class WebIframeInAppMessageBridge implements InAppMessageBridge {
  constructor(private readonly hackleClient: BrowserHackleClient, private readonly inAppMessageUi: InAppMessageUi) {
    this.openUrl = this.openUrl.bind(this)
    this.trackClick = this.trackClick.bind(this)
    this.handleUrl = this.handleUrl.bind(this)
    this.closeInAppMessage = this.closeInAppMessage.bind(this)
    this.getUser = this.getUser.bind(this)
    this.setUser = this.setUser.bind(this)
    this.setUserId = this.setUserId.bind(this)
    this.setDeviceId = this.setDeviceId.bind(this)
    this.setUserProperty = this.setUserProperty.bind(this)
    this.setUserProperties = this.setUserProperties.bind(this)
    this.updateUserProperties = this.updateUserProperties.bind(this)
    this.updatePushSubscriptions = this.updatePushSubscriptions.bind(this)
    this.updateSmsSubscriptions = this.updateSmsSubscriptions.bind(this)
    this.updateKakaoSubscriptions = this.updateKakaoSubscriptions.bind(this)
    this.setPhoneNumber = this.setPhoneNumber.bind(this)
    this.unsetPhoneNumber = this.unsetPhoneNumber.bind(this)
    this.track = this.track.bind(this)
    this.getDisplayedInAppMessageView = this.getDisplayedInAppMessageView.bind(this)
    this.variation = this.variation.bind(this)
    this.variationDetail = this.variationDetail.bind(this)
    this.isFeatureOn = this.isFeatureOn.bind(this)
    this.featureFlagDetail = this.featureFlagDetail.bind(this)
    this.remoteConfig = this.remoteConfig.bind(this)
  }
  // --------------------- InAppMessageBridge supported methods ---------------------
  private getCurrentInAppMessageView(): InAppMessageView {
    const currentView = ObjectUtil.requiredNotNullOrUndefined(
      this.inAppMessageUi.currentMessageView,
      () => "Failed to get current in-app message view because current in-app message view is not found"
    )
    return currentView
  }

  openUrl(url: string | HackleInAppMessageLink): void {
    const view = this.getCurrentInAppMessageView()
    const timestamp = view.ui.clock.currentMillis()
    const event = toInAppMessageActionViewEvent({ url, timestamp })

    view.handle(event, ["ACTION"])
  }

  trackClick(elementId?: string): void {
    const view = this.getCurrentInAppMessageView()
    const timestamp = view.ui.clock.currentMillis()
    const event = toInAppMessageActionViewEvent({ elementId, timestamp })

    view.handle(event, ["TRACK"])
  }

  handleUrl(url: string | HackleInAppMessageLink, elementId?: string): void {
    const view = this.getCurrentInAppMessageView()
    const timestamp = view.ui.clock.currentMillis()
    const event = toInAppMessageActionViewEvent({ url, elementId, timestamp })

    view.handle(event, ["TRACK", "ACTION"])
  }

  private hideInAppMessage(hideDurationMillis: number): void {
    const view = this.getCurrentInAppMessageView()
    const timestamp = view.ui.clock.currentMillis()
    const event = toInAppMessageHiddenActionViewEvent({ timestamp, hideDurationMillis })

    view.handle(event, ["TRACK", "ACTION"])
  }

  private closeInAppMessageView(): void {
    this.getCurrentInAppMessageView()?.close()
  }

  closeInAppMessage(): void
  closeInAppMessage(hideDurationMinutes: number): void
  closeInAppMessage(hideOneDayOrClose: boolean): void
  closeInAppMessage(hideDuration?: boolean | number): void {
    switch (typeof hideDuration) {
      case "boolean":
        const hideOneDay = hideDuration
        hideOneDay ? this.hideInAppMessage(IAM_HIDE_EXPIRATION_MILLIS) : this.closeInAppMessageView()
        break
      case "number":
        const hideDurationMinutes = hideDuration
        const hideDurationMillis = TimeUtil.minutesToUnit(hideDurationMinutes, "milliseconds")
        this.hideInAppMessage(hideDurationMillis)
        break
      default:
        this.closeInAppMessageView()
    }
  }
  // --------------------- hackleClient supported methods ---------------------
  getUser(): User {
    return this.hackleClient.getUser()
  }

  setUser(user: User): Promise<void> {
    return this.hackleClient.setUser(user)
  }

  setUserId(userId: string | number | undefined | null): Promise<void> {
    return this.hackleClient.setUserId(userId)
  }

  setDeviceId(deviceId: string): Promise<void> {
    return this.hackleClient.setDeviceId(deviceId)
  }

  setUserProperty(key: string, value: any): Promise<void> {
    return this.hackleClient.setUserProperty(key, value)
  }

  setUserProperties(properties: Properties): Promise<void> {
    return this.hackleClient.setUserProperties(properties)
  }

  updateUserProperties(operations: PropertyOperations): Promise<void> {
    return this.hackleClient.updateUserProperties(operations)
  }

  updatePushSubscriptions(operations: HackleSubscriptionOperations): Promise<void> {
    return this.hackleClient.updatePushSubscriptions(operations)
  }

  updateSmsSubscriptions(operations: HackleSubscriptionOperations): Promise<void> {
    return this.hackleClient.updateSmsSubscriptions(operations)
  }

  updateKakaoSubscriptions(operations: HackleSubscriptionOperations): Promise<void> {
    return this.hackleClient.updateKakaoSubscriptions(operations)
  }

  setPhoneNumber(phoneNumber: string): Promise<void> {
    return this.hackleClient.setPhoneNumber(phoneNumber)
  }

  unsetPhoneNumber(): Promise<void> {
    return this.hackleClient.unsetPhoneNumber()
  }

  track(event: HackleEvent | string, user?: User | string): void {
    this.hackleClient.track(event, user)
  }

  getDisplayedInAppMessageView(): HackleInAppMessageView | null {
    return this.hackleClient.getDisplayedInAppMessageView()
  }

  variation(experimentKey: number, user?: User | string, defaultVariation?: string): string {
    return this.hackleClient.variation(experimentKey, user, defaultVariation)
  }

  variationDetail(experimentKey: number, user?: User | string, defaultVariation?: string): Decision {
    return this.hackleClient.variationDetail(experimentKey, user, defaultVariation)
  }

  isFeatureOn(featureKey: number, user?: User | string): boolean {
    return this.hackleClient.isFeatureOn(featureKey, user)
  }

  featureFlagDetail(featureKey: number, user?: User | string): FeatureFlagDecision {
    return this.hackleClient.featureFlagDetail(featureKey, user)
  }

  remoteConfig(user?: User | string): HackleRemoteConfig {
    return this.hackleClient.remoteConfig(user)
  }

  getSessionId(): string {
    return this.hackleClient.getSessionId()
  }

  resetUser(): Promise<void> {
    return this.hackleClient.resetUser()
  }

  showUserExplorer(): void {
    this.hackleClient.showUserExplorer()
  }

  hideUserExplorer(): void {
    this.hackleClient.hideUserExplorer()
  }

  isOptOutTracking(): boolean {
    return this.hackleClient.isOptOutTracking()
  }

  setOptOutTracking(optOut: boolean): void {
    this.hackleClient.setOptOutTracking(optOut)
  }
  // --------------------- unsupported methods ---------------------
  trackPageView(option?: PageView): void {
    Logger.log.warn("trackPageView is not supported in WebIframeInAppMessageBridge")
  }
  setCurrentPage(page: HacklePage): void {
    Logger.log.warn("setCurrentPage is not supported in WebIframeInAppMessageBridge")
  }
  close(): void {
    Logger.log.warn("close is not supported in WebIframeInAppMessageBridge")
  }
  onReady(block: () => void, timeout?: number | undefined): void {
    Logger.log.warn("onReady is not supported in WebIframeInAppMessageBridge")
  }
  onInitialized(config?: { timeout?: number | undefined } | undefined): Promise<{ success: boolean }> {
    Logger.log.warn("onInitialized is not supported in WebIframeInAppMessageBridge")
    return Promise.resolve({ success: false })
  }
  getExperiment(experimentKey: number): HackleExperiment | undefined {
    Logger.log.warn("getExperiment is not supported in WebIframeInAppMessageBridge")
    return undefined
  }
  setInAppMessageListener(listener: HackleInAppMessageListener | null): void {
    Logger.log.warn("setInAppMessageListener is not supported in WebIframeInAppMessageBridge")
  }
  fetch(): Promise<void> {
    Logger.log.warn("fetch is not supported in WebIframeInAppMessageBridge")
    return Promise.resolve()
  }
}
